import { getCsrfToken, signIn, signOut, getSession } from 'next-auth/react'
import type {
    SIWEVerifyMessageArgs,
    SIWECreateMessageArgs,
    SIWESession,
} from '@reown/appkit-siwe'
import { createSIWEConfig, formatMessage } from '@reown/appkit-siwe'
import { supportedChains } from './wagmi-config'
import { isEvmNamespace, getTrackedAddress } from './chain-namespace-tracker'

// All supported chain IDs from wagmi config
const chains = supportedChains.map(c => c.id)

// Session cache key for localStorage backup
const SIWE_SESSION_CACHE_KEY = 'siwe-session-cache'
// Track if we've verified the session at least once this page load
const SESSION_VERIFIED_KEY = 'siwe-session-verified'

// Helper to get cached session from localStorage
function getCachedSession(): SIWESession | null {
    if (typeof window === 'undefined') return null
    try {
        const cached = localStorage.getItem(SIWE_SESSION_CACHE_KEY)
        if (cached) {
            const parsed = JSON.parse(cached)
            // Verify the cached session has required fields
            if (parsed.address && typeof parsed.chainId === 'number') {
                console.log('[SIWE] Found cached session:', parsed.address.substring(0, 10) + '...')
                return parsed as SIWESession
            }
        }
    } catch {
        // Ignore parse errors
    }
    return null
}

// Helper to cache session to localStorage
function cacheSession(session: SIWESession | null): void {
    if (typeof window === 'undefined') return
    try {
        if (session) {
            localStorage.setItem(SIWE_SESSION_CACHE_KEY, JSON.stringify(session))
            console.log('[SIWE] Cached session:', session.address.substring(0, 10) + '...')
        } else {
            localStorage.removeItem(SIWE_SESSION_CACHE_KEY)
            console.log('[SIWE] Cleared cached session')
        }
    } catch {
        // Ignore storage errors
    }
}

// Check if session has been verified this page load
function isSessionVerified(): boolean {
    if (typeof window === 'undefined') return false
    return sessionStorage.getItem(SESSION_VERIFIED_KEY) === 'true'
}

// Mark session as verified for this page load
function markSessionVerified(): void {
    if (typeof window === 'undefined') return
    sessionStorage.setItem(SESSION_VERIFIED_KEY, 'true')
}

export const siweConfig = createSIWEConfig({
    // Required functions
    getMessageParams: async () => ({
        domain: typeof window !== 'undefined' ? window.location.host : '',
        uri: typeof window !== 'undefined' ? window.location.origin : '',
        chains, // Include all chains so signature is valid for all
        statement: 'Sign in to TokenKickstarter',
    }),

    createMessage: ({ address, ...args }: SIWECreateMessageArgs) => {
        // Skip SIWE message creation for non-EVM chains
        if (!isEvmNamespace()) {
            console.log('[SIWE] Non-EVM chain detected, returning bypass message')
            return '__NON_EVM_BYPASS__'
        }
        return formatMessage(args, address)
    },

    getNonce: async () => {
        // Skip nonce fetch for non-EVM chains
        if (!isEvmNamespace()) {
            console.log('[SIWE] Non-EVM chain — skipping nonce')
            return 'non-evm-bypass'
        }
        const nonce = await getCsrfToken()
        if (!nonce) {
            throw new Error('Failed to get nonce!')
        }
        return nonce
    },

    getSession: async () => {
        console.log('[SIWE] getSession called')

        // For non-EVM chains, bypass SIWE by returning a synthetic session
        // This prevents the "Sign this message" prompt for Solana, Tron, TON
        if (!isEvmNamespace()) {
            const addr = getTrackedAddress()
            if (addr) {
                console.log('[SIWE] Non-EVM chain — returning bypass session for', addr.substring(0, 10) + '...')
                return { address: addr, chainId: chains[0] || 1 } as SIWESession
            }
            console.log('[SIWE] Non-EVM chain — no address yet, returning null')
            // No address available yet (first connection) — fall through
            // verifyMessage will auto-approve below
        }

        // Get cached session first - this is synchronous and instant
        const cachedSession = getCachedSession()

        // If we already verified the session this page load and have cache, return it immediately
        // This prevents redundant checks after initial verification
        if (isSessionVerified() && cachedSession) {
            console.log('[SIWE] Using verified cached session')
            return cachedSession
        }

        try {
            // Check NextAuth session
            const session = await getSession()
            console.log('[SIWE] NextAuth session:', session?.address ? session.address.substring(0, 10) + '...' : 'null')

            if (session?.address) {
                // Valid session from NextAuth - cache it, mark verified, and return
                const siweSession: SIWESession = {
                    address: session.address,
                    chainId: session.chainId || chains[0],
                }
                cacheSession(siweSession)
                markSessionVerified()
                return siweSession
            }

            // NextAuth returned null - but we might have a valid cached session
            // This can happen during hydration delays
            if (cachedSession) {
                console.log('[SIWE] NextAuth null but have cached session - waiting for hydration')

                // Wait for potential hydration and retry
                await new Promise(resolve => setTimeout(resolve, 500))
                const retrySession = await getSession()
                console.log('[SIWE] Retry session after 500ms:', retrySession?.address ? retrySession.address.substring(0, 10) + '...' : 'null')

                if (retrySession?.address) {
                    // Session hydrated - check if it matches our cache
                    if (retrySession.address === cachedSession.address) {
                        // Same address - keep using it
                        markSessionVerified()
                        return cachedSession
                    } else {
                        // Different address - clear old cache, use new session
                        console.log('[SIWE] Address changed, updating session')
                        const newSession: SIWESession = {
                            address: retrySession.address,
                            chainId: retrySession.chainId || chains[0],
                        }
                        cacheSession(newSession)
                        markSessionVerified()
                        return newSession
                    }
                } else {
                    // Still null after retry - but we have a cached session
                    // The user probably signed in before, trust the cache
                    // Don't mark as verified so we can check again later
                    console.log('[SIWE] Trusting cached session (NextAuth still hydrating)')
                    return cachedSession
                }
            }

            // No cached session and no NextAuth session - truly not signed in
            console.log('[SIWE] No session found')
            return null
        } catch (error) {
            console.error('[SIWE] getSession error:', error)
            // On error, try to return cached session to prevent false sign-out
            return cachedSession
        }
    },

    verifyMessage: async ({ message, signature }: SIWEVerifyMessageArgs) => {
        // Auto-approve non-EVM chains (bypass marker or namespace check)
        if (message === '__NON_EVM_BYPASS__' || !isEvmNamespace()) {
            console.log('[SIWE] Auto-approving non-EVM chain')
            return true
        }
        try {
            console.log('[SIWE] Verifying signature...')
            const success = await signIn('credentials', {
                message,
                redirect: false,
                signature,
                callbackUrl: '/',
            })
            console.log('[SIWE] Verification result:', success?.ok)
            return Boolean(success?.ok)
        } catch (error) {
            console.error('[SIWE] Verification error:', error)
            return false
        }
    },

    signOut: async () => {
        try {
            console.log('[SIWE] Signing out...')
            // Clear cached session and verification flag on explicit sign out
            cacheSession(null)
            if (typeof window !== 'undefined') {
                sessionStorage.removeItem(SESSION_VERIFIED_KEY)
            }
            await signOut({ redirect: false })
            return true
        } catch {
            return false
        }
    },

    // ===== CRITICAL: These options control re-signing behavior =====

    // Don't sign out when network/chain changes - this is the key setting!
    signOutOnNetworkChange: false,

    // Disable signOutOnAccountChange - AppKit detects wallet reconnection during
    // page hydration as an "account change" which triggers false sign-outs.
    // Our caching logic handles real account changes by comparing addresses.
    signOutOnAccountChange: false,

    // Don't sign out on disconnect - let the user reconnect and resume session
    signOutOnDisconnect: false,

    // Session refetch interval - check session every 5 minutes instead of on every render
    // This prevents rapid re-checks that can cause false sign-out states
    sessionRefetchIntervalMs: 5 * 60 * 1000, // 5 minutes

    // Nonce refetch interval - get new nonce only when needed, not constantly
    nonceRefetchIntervalMs: 5 * 60 * 1000, // 5 minutes

    // Keep SIWE enabled
    enabled: true,
})
